// various custom ag-grid renderer components

import Vue from 'vue';
import { HumanizeDateTimeHelper } from '@/js/filters-helpers';
import { DateTime } from 'luxon';
import BootstrapPhoneNumberInput from '@/components/shared/BootstrapPhoneNumberInput.vue';
import { nanoid } from 'nanoid';

// VUE COMPONENTS

// GENERAL RENDERERS
// ----------------------------------------------------------------------

const CompactDateTimeRenderer = Vue.extend({
  // for showing date/time in a compact format, with popover/tooltip to show more
  // displays things in LOCAL timezone?
  template: `
  <span 
    v-if="luxonDateTime" 
    :id="uniqueId"
    class="text-nowrap"
    v-b-tooltip="{ title: formattedFull, placement: 'left' }"
  >{{ formattedCompact }}</span>
  `,
  // TODO - maybe allow passing in a format string for the date/time? or a constant from DateTime such as DateTime.DATE_MED?
  // DOCS: https://moment.github.io/luxon/#/formatting
  computed: {
    uniqueId() {
      // we need unique IDs on the tooltip element, or else funky stuff happens
      const rowId = this.params?.data?.id || 'row' + nanoid();
      const colId = this.params?.colDef?.field || 'col' + nanoid();
      return `compact-dt-${rowId}-${colId}`;
    },
    luxonDateTime() {
      // return the DateTime object from params.value, if available
      if (!this.params || !this.params.value) {
        // console.log('CompactDateTimeRenderer: No params or value available');
        return null;
      }
      // we LIKELY have a JSDate... because ag-grid wants to work with JSDate objects (for filters) so we should have converted to JSDate in valueGetter for this column
      if (this.params.value instanceof Date) {
        // console.log('CompactDateTimeRenderer: params.value is a Date object');
        return DateTime.fromJSDate(this.params.value);
      }
      // if we have a string, try to parse it as ISO string
      if (typeof this.params.value === 'string') {
        // console.log('CompactDateTimeRenderer: params.value is a string, trying to parse as ISO');
        return DateTime.fromISO(this.params.value);
      }
      // otherwise, assume it's a DateTime object already?
      if (DateTime.isDateTime(this.params.value)) {
        // console.log('CompactDateTimeRenderer: params.value is already a DateTime object');
        return this.params.value;
      }
      // if we get here, we don't know what to do with the value, so return null
      console.error('CompactDateTimeRenderer: Unable to parse params.value as DateTime:', this.params.value);
      return null;
    },
    formattedCompact() {
      // the string to show in the grid cell
      return this.luxonDateTime ? this.luxonDateTime.toLocaleString(DateTime.DATE_SHORT) : ''; // DATE_SHORT is like 'MM/dd/yyyy'
    },
    formattedFull() {
      // the full string to show in the popover/tooltip
      if (!this.luxonDateTime) {
        return '';
      }
      // DATETIME_HUGE: 'Friday, October 14, 1983 at 1:30 PM Eastern Daylight Time'
      return this.luxonDateTime.toLocaleString(DateTime.DATETIME_HUGE);
    },
  },
});

// FOR CONTACT GRID (TUs)
// ----------------------------------------------------------------------
const ContactFullNameRenderer = Vue.extend({
  // this is the renderer for the main/first column in the ObjectGrid for Contacts
  template: `
  <span>
    <a 
      href="#" 
      @click.prevent="viewContact" 
      class="font-weight-600"
    >{{ params.valueFormatted || params.value }}</a>
    <base-popover-icon 
      v-if="params.data.email_issues_since" 
      icon="envelope-fill" 
      icon-variant="danger" 
      :title="emailIssuesPopoverText"
      class="ml-2"
    >
      There are delivery issues with this user's email address.  Please contact Supplios support for more information.
    </base-popover-icon>
  </span>
  `,
  computed: {
    contactId() {
      // return the contact ID from params.data, if available
      return this.params?.data?.id || null;
    },
    emailIssuesSinceFormatted() {
      // format the email issues date if available
      const emailIssuesSince = this.params?.data?.email_issues_since;
      return emailIssuesSince ? DateTime.fromISO(emailIssuesSince).toLocaleString(DateTime.DATE_SHORT) : '';
    },
    emailIssuesPopoverText() {
      // return the formatted email issues text for the popover
      const emailIssuesSince = this.params?.data?.email_issues_since;
      return emailIssuesSince ? `Email issues since ${DateTime.fromISO(emailIssuesSince).toLocaleString(DateTime.DATE_MED)}` : '';
    },
  },
  methods: {
    viewContact() {
      // Use the ObjectGrid's $emit method directly
      // console.log('ContactFullNameRenderer: Emitting viewContact event with contactId:', this.contactId, this.params.context.componentParent);
      if (this.contactId && this.params.context.componentParent) {
        // Emit the event directly through the ObjectGrid component (componentParent)
        this.params.context.componentParent.$emit('view-contact', this.contactId);
        // console.log('ContactFullNameRenderer: Event emitted successfully');
      } else {
        console.error('ContactFullNameRenderer: Unable to emit event - either contactId is missing or componentParent is not available');
      }
    },
  },
});

const BootstrapPhoneNumberRenderer = Vue.extend({
  components: {
    BootstrapPhoneNumberInput,
  },
  template: `
    <div class="h-100 d-flex flex-column align-items-center justify-content-center">
        <div
            v-for="(phoneNumber, idx) in phoneNumberArray"
            :key="idx"
            class="w-100"
            style="line-height: 1.5;"
        >
            <BootstrapPhoneNumberInput
                :phoneData="phoneNumber"
            ></BootstrapPhoneNumberInput>
        </div>
    </div>
  `,
  computed: {
    phoneNumberArray() {
      // normalizes a single phone number or list of phone numbers
      if (!this.params || !this.params.value) {
        return [];
      }
      return Array.isArray(this.params.value) ? this.params.value : [this.params.value];
    },
  },
});

const portalAccessConfig = {  // TODO - get this from backend somehow -- in the grid context probably
  full: {
    variant: 'soft-success',
    label: 'Active',
  },
  open_invite: {
    variant: 'soft-primary',
    label: 'Open Invite',
  },
  expired_invite: {
    variant: 'soft-warning',
    label: 'Expired Invite',
  },
  on_hold: {
    variant: 'soft-danger',
    label: 'Deactivated',
  },
  invite_ready: {
    variant: 'soft-info',
    label: 'Invite Ready',
  },
  none: {
    variant: 'soft-secondary',
    label: 'No Access',
  },
};

const PortalAccessBadgeRenderer = Vue.extend({
  template: `
  <span>
    <b-badge
      v-if="badgeData"
      :key="badgeData.key"
      :variant="badgeData.variant"
      class="text-uppercase"
    >
      {{ badgeData.label }}
    </b-badge>
    <span v-else>{{ params.valueFormatted || params.value }}</span>
  </span>
  `,
  computed: {
    badgeData() {
      // NOTE: this may be used in different versions of portal_access col... could be portal_access, portal_access_anno, or portal_access_property depending on the API and how you set up the column 
      // also used in the filter rendering for those cols, so it needs to be able to handle both the row data version (which might have more info) and the filter option version (which might just be the key)
      // in the case of filter rendering, params.value will just be the key (like 'full', 'open_invite', etc.)
      // also, in filter rendering, the value could be the special ag-grid value of '(Select All)' or '(Blanks)'... we should just return null for those so it doesn't try to look them up in the config?
      // seems that in both cases we can just look at params.value for the key, and it should work... 
      const portalAccessKey = this.params?.value || ''; // we use params.value for both row data and filter option rendering, so it should work for both versions of the portal_access col (as long as we set the field name correctly in colDef)
      // const fieldName = this.params?.colDef?.field || '';
      // const portalAccessKey = this.params?.data?.[fieldName] || ''; // we use the field name from colDef to get the value from data, so it works no matter which version of portal_access we are using (as long as we set the field name correctly in colDef)
      // const portalAccessKey = this.params?.data?.portal_access || ''; // if we don't have data for that key, just show the valueFormatted or value (for the special case filter options)
      return portalAccessConfig[portalAccessKey] ? { key: portalAccessKey, ...portalAccessConfig[portalAccessKey] } : null;
    },
  },
  // mounted() {
  //   console.log('PortalAccessBadgeRenderer mounted with params:', this.params);
  // }
});

const EmailAddressRenderer = Vue.extend({
  template: `
      <a 
        :href="emailLink" 
        class="text-decoration-none"
      >{{ params.value }}</a>
  `,
  computed: {
    emailLink() {
      // return the full link/mailto link for the email address
      return this.params?.value ? `mailto:${this.params.value}` : '';
    },
  },
});

const DefaultContactRenderer = Vue.extend({
  // for showing the TenantRoles a contact holds on the Contact Grid ('default_contact_for' column) - labels come from the column refData (role names)
  // this handles value being an array of slugs (for the row data) OR a single slug (for filter options rendering)
  // params.value will be a list of slugs in row data, but only a single slug in filter rendering
  template: `
    <span class="d-inline-flex flex-wrap gap-1">
      <b-badge
        v-for="(slug, idx) in normalizedValue"
        :key="idx"
        class="text-uppercase font-weight-500"
        variant="soft-secondary"
      >
        {{ getLabel(slug) }}
      </b-badge>
    </span>
  `,
  computed: {
    // this is just a computed property to get the refData from params
    normalizedValue() {
      // we normalize the value to always be an array, even if it's a single slug (like in filter rendering)
      return Array.isArray(this.params.value) ? this.params.value : [this.params.value].filter(Boolean); // filter out any falsy values in case we just have '' or null
    },
    refData() {
      return this.params?.colDef?.refData || {};
    },
  },
  methods: {
    getLabel(slug) {
      // return the label for the slug from refData  (NOTE: does't work... look at TenantRolesRenderer for how to do it)
      return this.refData[slug] || slug;
    },
  },
});

const TenantRolesRenderer = Vue.extend({
  // for showing the TenantRoles a contact holds on the Contact Grid ('default_contact_for' column) - labels come from the column refData (role names)
  // NEW VERSION - replaces DefaultContactRenderer for tenant role slugs
  // this handles value being an array of slugs (for the row data) OR a single slug (for filter options rendering)
  // params.value will be a list of slugs in row data, but only a single slug in filter rendering
  template: `
    <span class="d-inline-flex flex-wrap gap-1">
      <b-badge
        v-for="(slug, idx) in normalizedValue"
        :key="idx"
        class="text-uppercase font-weight-500"
        variant="soft-secondary"
      >
        {{ getLabel(slug) }}
      </b-badge>
    </span>
  `,
  computed: {
    // this is just a computed property to get the refData from params
    normalizedValue() {
      // we normalize the value to always be an array, even if it's a single slug (like in filter rendering)
      return Array.isArray(this.params.value) ? this.params.value : [this.params.value].filter(Boolean); // filter out any falsy values in case we just have '' or null
    },
    refData() {
      return this.params?.colDef?.refData || {};  // this.params.colDef is there when we are in a cell, but not a filter, apparently -- but this.params.valueFormatted is
    },
  },
  methods: {
    getLabel(slug) {
      // console.log('getting label in TenantRolesRenderer for slug:', slug, 'with params:', this.params, 'and refData:', this.refData);
      // return the label for the slug from refData
      return this.refData[slug] || this.params.valueFormatted || slug;  
      // this.refData[slug] is used when we are rendering in a cell
      // this.params.valueFormatted is used when we are rendering in a filter
      // slug is the fallback (which gets used for things like '(Select All)' in the filter)
    },
  },
});

const CompanySiteTypeRenderer = Vue.extend({
  template: `
    <span class="d-inline-flex flex-wrap gap-1">
      <b-badge
        v-for="(id, idx) in normalizedValue"
        :key="idx"
        class="text-uppercase font-weight-500"
        variant="soft-secondary"
      >
        {{ getLabel(id) }}
      </b-badge>
    </span>
  `,
  computed: {
    // this is just a computed property to get the refData from params
    normalizedValue() {
      // we normalize the value to always be an array, even if it's a single slug (like in filter rendering)
      return Array.isArray(this.params.value) ? this.params.value : [this.params.value].filter(Boolean); // filter out any falsy values in case we just have '' or null
    },
    refData() {
      return this.params?.colDef?.refData || {};  // this.params.colDef is there when we are in a cell, but not a filter, apparently -- but this.params.valueFormatted is
    },
  },
  methods: {
    getLabel(id) {
      // console.log('getting label in TenantRolesRenderer for id:', id, 'with params:', this.params, 'and refData:', this.refData);
      // return the label for the id from refData
      return this.refData[id] || this.params.valueFormatted || id;  
      // this.refData[id] is used when we are rendering in a cell
      // this.params.valueFormatted is used when we are rendering in a filter
      // id is the fallback (which gets used for things like '(Select All)' in the filter)
    },
  },
});

const ActionCellRenderer = Vue.extend({
  template: `
        <v-popover
            offset="8"
            :open.sync="isOpen"
            auto-hide
            :handleResize="false"
            :disabled="!isEnabled"
            placement="left"
            popover-inner-class="popover-inner"
            @show="onShow"
            @apply-show="onApplyShow"
        >  
            <!-- This will be the popover target (for the events and position) -->
            <!-- <b-link class="text-secondary">
                <b-iconstack font-scale="1.5" class="tooltip-target">
                    <b-icon stacked icon="three-dots" variant="secondary" scale="0.75" class=""></b-icon>
                    <b-icon stacked icon="circle" variant="secondary" class=""></b-icon>
                </b-iconstack>
            </b-link> -->
            <b-button 
              :id="buttonId"
              ref="actionTriggerButton"
              size="sm" 
              variant="sharp-secondary" 
              class="rounded-circle"
              style="padding: 3px 5px; min-width: 28px; min-height: 28px;"
              @click="togglePopover"
              :disabled="isLoading || !isEnabled"
            >
                <!-- <b-icon icon="three-dots-vertical" variant="dark" aria-hidden="true"></b-icon> -->
                <!-- <span class="special-text">Actions</span> -->
                <b-icon icon="three-dots" variant="dark" aria-hidden="true"></b-icon>
            </b-button>
        
            <!-- This will be the content of the popover -->
            <template slot="popover">
              <div class="min-width-100">
                <div v-if="isLoading || localActions.length === 0" class="w-100 d-flex align-items-center justify-content-center p-3" style="min-height: 50px;">
                    <b-spinner v-if="isLoading" small type="grow" label="Loading..."></b-spinner>
                    <span v-else class="text-muted">No actions available</span>
                </div>
                <div v-else key="action-list" class="w-100 p-1 d-flex flex-column gap-1" style="min-width: 150px;">
                  <div class="font-weight-600 px-2 pb-1 border-bottom">Actions</div>
                  <ul ref="actionMenu" role="menu" tabindex="-1" :aria-labelledby="buttonId" class="m-0 p-0">
                    <li v-for="(action, actionIndex) in localActions" :key="action.key" role="presentation" class="">
                      <b-button 
                        ref="actionButtons"
                        role="menuitem"
                        size="sm" 
                        block 
                        variant="minimal-secondary"
                        class="text-left mt-0"
                        :tabindex="actionIndex === 0 ? '0' : '-1'"
                        @click="handleActionClick(action)"
                      >
                        <div class="d-flex gap-3 align-items-center justify-content-start">
                          <b-icon v-if="action.bsIcon" :icon="action.bsIcon" :variant="action.variant" font-scale="1" class="mr-2"></b-icon>
                          <span>{{ action.label }}</span>
                        </div>
                      </b-button>
                    </li>
                  </ul>
                </div>
              </div>
            </template>
        </v-popover>
    `,
  data() {
    return {
      isOpen: false,
      isLoading: false,
      isEnabled: true, // make dynamic later, set in mounted
      localActions: [], // the final list of actions to show
    };
  },
  computed: {
    buttonId() {
      // we need unique IDs on the popover element for various reasons
      return `action-button-${this.objectId || 'noid'}`;
    },
    rowData() {
      return this.params.data || {};
    },
    objectId() {
      return this.params.node.id || null; // in the parent, we have the getRowNodeId set to use data.id
    },
    parentGrid() {
      // helper to get the parent object grid component
      return this.params?.context?.componentParent || null;
    },
  },
  methods: {
    // see docs for v-popover here:  https://github.com/Akryum/floating-vue/blob/v2.1.3/README.md
    togglePopover() {
      if (this.isOpen) {
        this.closePopover();
      } else {
        this.showPopover();
      }
    },
    showPopover() {
      this.isOpen = true;
      // focusMenu in apply-show event after actions are loaded
    },
    closePopover() {
      this.isOpen = false;
      // this.focusTrigger();  // this seems to work, but we won't bother for now since focusMenu is not reliable
    },
    focusMenu() {
      // focus the action menu for keyboard navigation
      this.$nextTick(() => {
        const menu = this.$refs.actionMenu;
        // const buttons = this.$refs.actionButtons;
        if (menu) {
          // console.log('found action menu:', menu);
          const buttons = menu.querySelectorAll('button');
          if (buttons && buttons.length > 0) {
            // console.log('Focusing first action button in menu:', buttons[0]);
            buttons[0].focus(); // it kinda works, grid seems to fight with it the first time it opens???
            // let's not bother with it much
          } else {
            console.error('ActionCellRenderer: Unable to focus action button in menu, no buttons found.');
          }
        } else {
          console.error('ActionCellRenderer: Unable to focus action menu, ref not found.');
        }
      });
    },
    focusTrigger() {
      // focus the popover trigger button
      this.$nextTick(() => {
        const button = this.$refs.actionTriggerButton;
        if (button) {
          // console.log('Focusing action trigger button:', button);
          button.focus();
        } else {
          console.error('ActionCellRenderer: Unable to focus action trigger button, ref not found.');
        }
      });
    },
    async onShow() {
      // console.log('ActionCellRenderer: Popover shown (@show), loading actions if needed.');
      this.isLoading = true;
      await this.loadActions();
      this.isLoading = false;
    },
    async onApplyShow() {
      // console.log('ActionCellRenderer: Popover shown (@apply-show), loading actions if needed.');
      // this.focusMenu();  // it sometimes works, sometimes doesn't... grid seems to fight with it the first time it opens???  let's not bother for now
    },
    async loadActions() {
      // parent of the parent grid (whoever is using the ObjectGrid component)
      // should pass in an array of actions, OR a function that returns array of actions via the rowActions prop on ObjectGrid
      // if function, then we use that here, passing in the expected params (just use the exact same params object we have in this component!)
      // should be ag-grid ICellRendererParams object:  https://www.ag-grid.com/archive/25.3.0/vue-grid/component-cell-renderer/#cell-renderer-component-3
      // const rowActionsProp = this.parentGrid?.[ROW_ACTIONS_KEY] || null;
      // WE HAVE cellRendererParams on this component, which has either 'actions' (array) or 'getActions' (function)
      const actionsArray = this.params?.actions && Array.isArray(this.params.actions) ? this.params.actions : null;
      const actionsFunction = this.params?.getActions ? this.params.getActions : null;
      // can be an array, or function that returns an array of action objects with:
      // { key: string, label: string, bsIcon: string, variant: string }
      // key is the action key, will be emitted in the 'action-click' event when clicked (with other stuff) -- the other things are for display
      // by having this be a function, we can have dynamic actions per row if needed, based on row data, other things in parent, etc.
      // can also be async function if needed
      if (!actionsArray && !actionsFunction) {
        this.localActions = [];
        return; // just return no actions
      }
      let rawActions = [];
      if (actionsArray) {
        rawActions = actionsArray;
      } else if (actionsFunction) {
        rawActions = await actionsFunction(this.params);
      }
      // we make sure it has valid/expected structure here, no matter how we got the rawActions (have to check types again since we might have called function)
      if (!rawActions) {
        this.localActions = [];
        return;
      } else if (!Array.isArray(rawActions)) {
        console.error('ActionCellRenderer: actionCellGetter did not return an array of actions:', rawActions);
        this.localActions = [];
        return;
      } else if (rawActions.length === 0) {
        this.localActions = [];
        return;
      } else {
        // we have some actions, but make sure they have all the right stuff, validate and add defaults
        const preppedActions = [];
        rawActions.forEach((action) => {
          const { key, label, bsIcon, variant, ...otherActionObjectProps } = action;
          if (!key) {
            console.error('ActionCellRenderer: action is missing key, skipping action:', action);
            return;
          }
          const preppedAction = {
            key: key,
            label: label || action.key,
            variant: variant || 'secondary', // will just be used on the icon for now
            bsIcon: bsIcon || null, // no icon shown if empty
            ...otherActionObjectProps, // pass all the other props through too so you have them when you relay this up
          };
          preppedActions.push(preppedAction);
        });
        this.localActions = preppedActions;
        // this.focusMenu();  // not very important, its annoying to make it work
      }
    },
    handleActionClick(action) {
      // emit an event to the parent grid when an action is clicked
      const payload = { ...this.params }; // just pass it all up... let ObjectGrid handle it, it will relay up to its own parent with whatever makes sense there
      // don't use parentGrid.$emit here... that's too indirect... call method on parent grid, then it will relay it up to its own parent
      if (this.parentGrid?.handleActionClick) {
        this.parentGrid.handleActionClick(action, payload);
      }
      this.closeAndReset();
    },
    closeAndReset() {
      // DRY function to close popover & reset all things (in that order, so it doesn't flash other things before closing)
      // need to reset things because component doesn't get destroyed for some time after closing
      this.closePopover();
      // this.showConfirmDelete = false;   // only set this if you have swappable content
    },
  },
});

// FOR FLOW OBJECT GRIDS
// ----------------------------------------------------------------------

const SmStateRenderer = Vue.extend({
  // pass in
  computed: {
    smStateObj() {
      return this.params?.context?.sm_states?.[this.params.value] ?? {};
    },
    badgeVariant() {
      return this.smStateObj.color_variant;
    },
    badgeTitle() {
      return this.smStateObj.badge_title;
    },
  },
  template: `
        <div class="text-center">
            <b-badge v-if="badgeVariant && badgeTitle" :variant="badgeVariant">{{badgeTitle}}</b-badge>
            <span v-else>{{ params.valueFormatted || params.value }}</span>
        </div>
    `,
});

// NOTE: EXPERIMENTAL
const ProgressBarComponent = Vue.extend({
  template: `
        <div class="w-100" style="padding-top: 13px; padding-bottom: 13px;">
            <b-progress :value="intValue" :max="100" height="1rem">
                <b-progress-bar :value="intValue" :label="intValue + '%'" :variant="color"></b-progress-bar>
            </b-progress>
        </div>
    `,
    computed: {
      intValue() {
        return this.params.value ? this.params.value : 0;
      },
      color() {
        return this.params.value && this.params.value >= 100 ? 'success' : 'primary';
      }
      // numberValue() {
      //   return this.params.value ? Number(this.params.value.value) : 0;
      // },
      // color() {
      //   return this.params.value ? this.params.value.color : 'primary';
      // }
  },
});

// NOTE: EXPERIMENTAL
const ProgressStepComponent = Vue.extend({
  template: `
        <div class="w-100" style="padding-top: 13px; padding-bottom: 13px;">
            <div class="d-flex align-items-center justify-content-center gap-1 rounded-lg overflow-hidden">
                <div v-for="n in totalSegments" :key="n" class="flex-fill" style="height: 1rem; border-radius: 1px; background-color: var(--primary-100);">
                    <div v-if="n <= valueSegments" class="h-100" :class="'bg-primary'" style="border-radius: 1px;"></div>
                </div>
            </div>
        </div>
    `,
    computed: {
      totalSegments() {
        return 5;
      },
      valueSegments() {
        return this.params.value ? Math.round((this.params.value / 100) * this.totalSegments) : 0;
      }
      // numberValue() {
      //   return this.params.value ? Number(this.params.value.value) : 0;
      // },
      // color() {
      //   return this.params.value ? this.params.value.color : 'primary';
      // }
  },
});

const SsrmStatusBarComponent = Vue.extend({
  // has access to this.params - which is { api, columnApi, context }
  template: `<div class="py-2">
        <span v-if="loadingRows" key="isLoading">Loading...</span>
        <span v-else key="noLoading" class="font-weight-bold d-flex gap-4 align-items-center justify-content-start">
            <span class="font-weight-bold">Total Items: {{ totalItemCount }}</span>
            <span v-if="totalItemCount !== filteredItemCount" class="text-primary-darker">
                <b-icon icon="filter-circle" class="mr-1"></b-icon>
                Filtered Items: {{ filteredItemCount }}
            </span>
        </span>
    </div>`,
  computed: {
    loadingRows() {
      return this.params.context.componentParent.loadingRows;
    },
    totalItemCount() {
      return this.params.context.componentParent.totalItemCount;
    },
    filteredItemCount() {
      return this.params.context.componentParent.filteredItemCount;
    },
  },
});

const EmptyGridOverlayComponent = Vue.extend({
  template: `
        <div class="ag-overlay-loading-center opacity-60 p-4" style="background-color: var(--primary-100);">
            <div class="font-size-100 font-weight-500 text-uppercase">{{ params.message }}</div>
            <div v-if="params.componentParent.gridHasAnyFilters" class="mt-3">(filters are applied)</div>
        </div>
    `,
});

const PrefetchedOverdueNodesRenderer = Vue.extend({
  // params.value is an ARRAY of overdue nodes (but might not have it if row grouping is on!)
  // if there are more than MAX_INLINE_NODES, show a compact summary in the cell, with the full list in a popover (on click)
  template: `
        <div v-if="!showSummary" class="py-2 d-flex flex-column gap-2 align-items-center justify-content-center" style="line-height: 1.1;">
            <div v-for="node in nodes" :key="node.id" class="text-center">
                <div>{{ node.title }}</div>
                <div class="font-size-70 text-muted mt-1">Due {{ getRelativeTime(node.deadline) }}</div>
            </div>
        </div>
        <div v-else class="py-2 d-flex align-items-center justify-content-center" style="line-height: 1.1;">
            <v-popover
                offset="8"
                :open.sync="isOpen"
                auto-hide
                :handleResize="false"
                placement="left"
                popover-inner-class="popover-inner"
            >
                <!-- This will be the popover target (for the events and position) -->
                <b-link class="text-center d-block text-reset text-decoration-none" @click.prevent="isOpen = !isOpen">
                    <div class="text-wrap">{{ nodes.length }} overdue tasks</div>
                    <div class="font-size-70 mt-1 text-primary">View all <b-icon icon="chevron-down" scale="0.8"></b-icon></div>
                </b-link>

                <!-- This will be the content of the popover -->
                <template slot="popover">
                    <div class="p-1" style="min-width: 200px; max-width: 320px;">
                        <div class="font-weight-600 px-2 pb-1 mb-1 border-bottom">Overdue Tasks ({{ nodes.length }})</div>
                        <div class="d-flex flex-column gap-2 px-2 py-1" style="max-height: 300px; overflow-y: auto; line-height: 1.1;">
                            <div v-for="node in nodes" :key="node.id">
                                <div class="text-wrap">{{ node.title }}</div>
                                <div class="font-size-70 text-muted mt-1">Due {{ getRelativeTime(node.deadline) }}</div>
                            </div>
                        </div>
                    </div>
                </template>
            </v-popover>
        </div>
    `,
  data() {
    return {
      MAX_INLINE_NODES: 2,
      isOpen: false, // using .sync with open prop on v-popover
    };
  },
  computed: {
    nodes() {
      return this.params.value || [];
    },
    showSummary() {
      return this.nodes.length > this.MAX_INLINE_NODES;
    },
  },
  methods: {
    getRelativeTime(isoDateTime) {
      return HumanizeDateTimeHelper(isoDateTime);
    },
  },
});

const PrefetchedActiveNodesRenderer = Vue.extend({
  // params.value is an ARRAY of active nodes
  // if there are more than MAX_INLINE_NODES, show a compact summary in the cell, with the full list in a popover (on click)
  template: `
        <div v-if="!showSummary" class="py-2 d-flex flex-column gap-2 align-items-center justify-content-center" style="line-height: 1.1;">
            <div v-for="node in nodesEnhanced" :key="node.id" class="text-center">
                <div class="text-wrap">{{ node.title }}</div>
                <div
                    v-if="node.deadline"
                    class="font-size-70 mt-1"
                    :class="node.deadlineParams.isOverdue ? 'text-danger' : 'text-muted'"
                >
                    Due {{ node.deadlineParams.relativeTimeText }}
                </div>
            </div>
        </div>
        <div v-else class="py-2 d-flex align-items-center justify-content-center" style="line-height: 1.1;">
            <v-popover
                offset="8"
                :open.sync="isOpen"
                auto-hide
                :handleResize="false"
                placement="left"
                popover-inner-class="popover-inner"
            >
                <!-- This will be the popover target (for the events and position) -->
                <b-link class="text-center d-block text-reset text-decoration-none" @click.prevent="isOpen = !isOpen">
                    <div class="text-wrap">{{ nodesEnhanced.length }} active tasks</div>
                    <div v-if="overdueCount" class="font-size-70 mt-1 text-danger">{{ overdueCount }} overdue</div>
                    <div class="font-size-70 mt-1 text-primary">View all <b-icon icon="chevron-down" scale="0.8"></b-icon></div>
                </b-link>

                <!-- This will be the content of the popover -->
                <template slot="popover">
                    <div class="p-1" style="min-width: 200px; max-width: 320px;">
                        <div class="font-weight-600 px-2 pb-1 mb-1 border-bottom">Current Active Tasks ({{ nodesEnhanced.length }})</div>
                        <div class="d-flex flex-column gap-2 px-2 py-1" style="max-height: 300px; overflow-y: auto; line-height: 1.1;">
                            <div v-for="node in nodesEnhanced" :key="node.id">
                                <div class="text-wrap">{{ node.title }}</div>
                                <div
                                    v-if="node.deadline"
                                    class="font-size-70 mt-1"
                                    :class="node.deadlineParams.isOverdue ? 'text-danger' : 'text-muted'"
                                >
                                    Due {{ node.deadlineParams.relativeTimeText }}
                                </div>
                            </div>
                        </div>
                    </div>
                </template>
            </v-popover>
        </div>
    `,
  data() {
    return {
      MAX_INLINE_NODES: 2,
      isOpen: false, // using .sync with open prop on v-popover
    };
  },
  computed: {
    showSummary() {
      return this.nodesEnhanced.length > this.MAX_INLINE_NODES;
    },
    overdueCount() {
      return this.nodesEnhanced.filter((node) => node.deadlineParams.isOverdue).length;
    },
    nodesEnhanced() {
      // add some extra info to each node in the list
      // NOTE - you might not always have params.value here -- like if showing with row grouping on
      return this.params.value
        ? this.params.value.map((node) => {
            const deadlineParams = this.getDeadlineParams(node);
            return {
              ...node,
              deadlineParams: deadlineParams,
            };
          })
        : [];
    },
  },
  methods: {
    getRelativeTime(isoDateTime) {
      return HumanizeDateTimeHelper(isoDateTime);
    },
    getDeadlineParams(node) {
      // below is basically same as HumanizeDateTimeHelper, but we need to return more info from it
      const params = {
        // defaults...
        relativeTimeText: '',
        isOverdue: false,
      };
      const deadlineIso = node.deadline;
      if (!deadlineIso) {
        return params;
      }
      const deadlineLuxon = deadlineIso ? (DateTime.isDateTime(deadlineIso) ? deadlineIso : DateTime.fromISO(deadlineIso)) : null;
      const timeDeltaSeconds = deadlineLuxon.diffNow().as('seconds'); // difference between value DateTime and now, in seconds  (NEGATIVE NUMBER == IN THE PAST, deadline is overdue)
      //console.log(timeDeltaSeconds);
      params.relativeTimeText = timeDeltaSeconds > -60 && timeDeltaSeconds <= 0 ? 'Just now' : deadlineLuxon.toRelative();
      // if it was less than 60 seconds ago, just say "just now", otherwise return the relative string
      params.isOverdue = timeDeltaSeconds < 0;
      return params;
    },
  },
});

const CompanyTagListRenderer = Vue.extend({
  template: `
    <span class="">
        <div class="px-1 d-flex flex-wrap gap-1" style="padding-top: 13px; padding-bottom: 13px;">
            <b-badge v-for="badge in badgeList" :key="badge.name" class="text-uppercase font-weight-500" :variant="badge.color_variant || 'soft-secondary'">{{ badge.name }}</b-badge>
            <span v-if="hasMoreText" class="text-muted font-size-70" style="line-height: normal;">{{ hasMoreText }}</span>
        </div>
    </span>
    `,
  computed: {
    maxTags() {
      return 3; // allow setting this in rendererParams, then just default to 3
    },
    badgeList() {
      // turn ids into badge objects -- this.params.value is an array of ids
      //console.log('CompanyTagListRenderer - params', this.params);
      const tagIdsToShow = Array.isArray(this.params.value) ? this.params.value.slice(0, this.maxTags) : [];
      return tagIdsToShow.map((id) => this.params?.context?.company_tags[id]);
    },
    hasMoreText() {
      const notShownTagIds = Array.isArray(this.params.value) ? this.params.value.slice(this.maxTags) : [];
      return notShownTagIds.length > 0 ? `+ ${notShownTagIds.length} more` : '';
    },
  },
});

const CompanyTagSingleRenderer = Vue.extend({
  template: `
    <div>
        <b-badge v-if="badge" class="text-uppercase font-weight-500" :variant="badge.color_variant || 'soft-secondary'">{{ badge.name }}</b-badge>
        <span v-else class="">{{ params.value }}</span>
    </div>
    `,
  computed: {
    badge() {
      // turn ids into badge objects -- this.params.value is an array of ids
      // NOTE - for the first value in filter list, the value will be '(Select All)' -- we need to handle this special case!  (they do the same in ag-grid docs)
      //console.log('CompanyTagSingleRenderer - computed for badge - params', this.params);
      if (this.params.value === '(Select All)') {
        return null;
      }
      return this.params?.context?.company_tags[this.params.value];
    },
  },
});


// TODO - add RFX Status Badge to this!
const RfxListRenderer = Vue.extend({
  template: `
    <div class="px-1 d-flex flex-column flex-wrap gap-2" style="padding-top: 13px; padding-bottom: 13px; line-height: 1;">
        <template v-if="rfxList.length">
            <div v-for="rfx in rfxList" class="d-flex align-items-center gap-2" :key="rfxKey(rfx)">
                <a v-if="rfx.url" :href="rfx.url" target="_blank" rel="noopener noreferrer">
                    {{ formatLabel(rfx) }}
                </a>
                <span v-else :key="rfxKey(rfx)">
                    {{ formatLabel(rfx) }}
                </span>
                <status-object-badge
                    :status-object="rfx.state_dict" 
                    text-uppercase
                    font-size="0.7rem"
                    title-field="badge_title"
                    no-wrap
                    class="ml-2"
                ></status-object-badge>
            </div>
        </template>
        <span v-else class="text-muted">{{ emptyText }}</span>
    </div>
    `,
  computed: {
    rfxList() {
      const value = this.params?.value;
      const rfxArray = Array.isArray(value) ? value : [];
      // add the badge stuff to it 
      return rfxArray;
    },
    emptyText() {
      // in a computed for translations eventually
      return '';
    },
  },
  methods: {
    rfxKey(rfx) {
      return rfx.id;
    },
    formatLabel(rfx) {
      // const label = rfx.rfx_type;
      // if (label) {
      //   return `${label}: ${rfx.title}`;
      // }
      return rfx.title || '';
    },
  },
});

const SplTagListRenderer = Vue.extend({
  // TODO - need better handling around displaying as BADGE or TEXT
  template: `
    <span class="">
        <div class="px-1 d-flex flex-wrap gap-1" style="padding-top: 13px; padding-bottom: 13px;">
            <b-badge v-for="badge in badgeList" :key="badge.name" class="text-uppercase font-weight-500" :variant="badge.color_variant || 'soft-secondary'">{{ badge.name }}</b-badge>
            <span v-if="hasMoreText" class="text-muted font-size-70" style="line-height: normal;">{{ hasMoreText }}</span>
        </div>
    </span>
    `,
  computed: {
    maxTags() {
      return 3;
    },
    badgeList() {
      // turn ids into badge objects -- this.params.value is an array of ids
      const tagIdsToShow = Array.isArray(this.params.value) ? this.params.value.slice(0, this.maxTags) : [];
      return tagIdsToShow.map((id) => this.params?.context?.spl_tags[id]);
    },
    hasMoreText() {
      const notShownTagIds = Array.isArray(this.params.value) ? this.params.value.slice(this.maxTags) : [];
      return notShownTagIds.length > 0 ? `+ ${notShownTagIds.length} more` : '';
    },
  },
});

const SplTagSingleRenderer = Vue.extend({
  // TODO - need better handling around displaying as BADGE or TEXT
  template: `
    <div>
        <b-badge v-if="badge" class="text-uppercase font-weight-500" :variant="badge.color_variant || 'soft-secondary'">{{ badge.name }}</b-badge>
        <span v-else class="">{{ params.value }}</span>
    </div>
    `,
  computed: {
    badge() {
      // turn ids into badge objects -- this.params.value is an array of ids
      // NOTE - for the first value in filter list, the value will be '(Select All)' -- we need to handle this special case!  (they do the same in ag-grid docs)
      console.log('SplTagSingleRenderer - computed for badge - params', this.params);
      if (this.params.value === '(Select All)') {
        return null;
      }
      return this.params?.context?.spl_tags[this.params.value];
    },
  },
});

export {
  CompactDateTimeRenderer,
  SmStateRenderer,
  ProgressBarComponent,
  ProgressStepComponent,
  SsrmStatusBarComponent,
  EmptyGridOverlayComponent,
  BootstrapPhoneNumberRenderer,
  PrefetchedOverdueNodesRenderer,
  PrefetchedActiveNodesRenderer,
  CompanyTagListRenderer,
  CompanyTagSingleRenderer,
  SplTagListRenderer,
  SplTagSingleRenderer,
  RfxListRenderer,
  ContactFullNameRenderer,
  PortalAccessBadgeRenderer,
  EmailAddressRenderer,
  DefaultContactRenderer,
  TenantRolesRenderer,
  ActionCellRenderer,
  CompanySiteTypeRenderer,
};
